import React from 'react';

import { MediaListItem } from 'application/components/MediaList/MediaListItem';
import { MediaListItemIconsUploader } from 'application/components/MediaList/MediaListItemIcons';
import VideoMediafilePreviewItemContent from 'application/components/VideoMediafilePreviewItem/VideoMediafileItemContent';
import FileUtils from 'application/utils/FileUtils';

import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';

import IconGripVertical from '@zvuk-b2b/react-uikit/ui/icons/GripVertical';
import TimeInput, { defaultTimeInputValidator, TIME_PATTERN } from '@zvuk-b2b/react-uikit/ui/TimeInput';

import { ELEMENT_IDS, MEDIA_FILE_TYPE } from '../consts';
import styles from './index.module.scss';
import { TEXTS } from '../../texts';
import { ExactTimeMediaUploaderItemProps } from './types';

const ExactTimeMediaUploaderItem: React.FC<ExactTimeMediaUploaderItemProps> = ({
  mediaType,
  item,
  time,
  player,
  isDragHandleVisible,
  onTimeChange,
  onTimeBlur,
  onPlayClick,
  onRemoveClick,
  onRetryClick,
}) => {
  const itemId = item.id || '';
  const filename = item.file?.name || item.title || item.filename || '';
  const source = FileUtils.getMediafileSource(item.sourcefiles);
  const fileUrl = source?.url ?? '';
  const extension = source?.extension;
  const isVideo = mediaType === AdCampaignMediaTypeEnum.Video;
  const isMediafilePlaying = player.fileName === filename && player.fileUuid === itemId;
  const mediaFileProgress = isMediafilePlaying ? player.progress : undefined;
  const isRemoveVisible = !item.error && !item.isLoading && itemId;
  const itemClassName = isDragHandleVisible
    ? styles.itemExact
    : `${styles.itemExact} ${styles.itemExactWithoutDragHandle}`;
  const videoMediaItemActionsClassName = item.error
    ? `${styles.videoMediaItemActions} ${styles.videoMediaItemActionsError}`
    : styles.videoMediaItemActions;

  return (
    <li
      data-id={itemId}
      className={itemClassName}>
      {isDragHandleVisible && (
        <span
          className={styles.dragHandle}
          data-testid={`${ELEMENT_IDS.dragHandlePrefix}_${itemId || filename}`}>
          <IconGripVertical />
        </span>
      )}
      {itemId && (
        <TimeInput
          elementId={`${ELEMENT_IDS.timeInputPrefix}_${itemId}`}
          containerClassName={styles.timeInput}
          maxLength={5}
          mask="00:00"
          pattern={TIME_PATTERN}
          placeholder={TEXTS.FILE_TIME_DEFAULT_VALUE}
          value={time ?? item.time ?? ''}
          inputValidator={defaultTimeInputValidator}
          onBlur={() => onTimeBlur(itemId)}
          onChange={(value) => onTimeChange(itemId, value)}
        />
      )}

      {isVideo ? (
        <div className={styles.videoMediaItem}>
          <div className={styles.videoMediaItemContent}>
            <VideoMediafilePreviewItemContent
              title={filename}
              uploading={item.isLoading}
              extension={extension}
              imgLink={item.previewUrl}
              duration={item.duration}
              error={item.error}
              url={fileUrl}
              posterContainerClassName={styles.posterContainer}
              posterClassName={styles.poster}
            />
          </div>
          <div className={videoMediaItemActionsClassName}>
            <MediaListItemIconsUploader
              id={`${ELEMENT_IDS.mediaIconsPrefix}_${itemId}`}
              isRemove={Boolean(isRemoveVisible)}
              isError={Boolean(item.error)}
              isMultiple
              onRemove={() => onRemoveClick(itemId)}
              onRetry={() => onRetryClick(itemId)}
            />
          </div>
        </div>
      ) : (
        <MediaListItem
          title={filename}
          isLoading={item.isLoading}
          isPaused={player.isPaused}
          isMediafilePlaying={isMediafilePlaying}
          progress={mediaFileProgress}
          type={MEDIA_FILE_TYPE}
          className={styles.mediaItem}
          titleClassName={styles.mediaItemTitle}
          progressSliderClassName={styles.progressSlider}
          onClickPlay={() => onPlayClick(itemId, filename, fileUrl)}
          onClickPause={player.pause}
          onClickResume={player.resume}
          onChangeProgress={player.changeProgress}>
          <MediaListItemIconsUploader
            id={`${ELEMENT_IDS.mediaIconsPrefix}_${itemId}`}
            isRemove={Boolean(isRemoveVisible)}
            isError={Boolean(item.error)}
            isMultiple
            onRemove={() => onRemoveClick(itemId)}
            onRetry={() => onRetryClick(itemId)}
          />
        </MediaListItem>
      )}
    </li>
  );
};

export default ExactTimeMediaUploaderItem;
